<template>
    <div class="break-normal" v-if="data">
        <div v-if="Object.keys(getComponents).length === 0" v-html="formatAll" />
        <Component v-else :is="{ template: formatAll, components: getComponents }"/>
    </div>
</template>

<script>
import VueMathjax from 'vue-mathjax-next'
import {formatImages, formatLinks} from "@/script"
import {defineComponent, h, markRaw} from "vue"
import coderunner from "@/components/UI/coderunner/Coderunner.vue"
import {replaceRutubeLinks, replaceRutubePrivateLinks} from "@/script/videoFilters"
import DOMPurify from 'dompurify';


export default defineComponent({
    name: "HTMLView",
    props: {
        data: String,
        mode: String,
        id: Number,
        book: String,
        replaceN: {
            type: Boolean,
            default: false
        },
        components: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        coderunner() {
            // Mark raw for avoiding performance overhead. See https://vuejs.org/api/reactivity-advanced#markraw
            return markRaw(coderunner)
        },
        getComponents() {
            let components = Object.assign(this.localComponents, this.components)

            if (this.isCoderunner) {
                components.coderunner = this.coderunner
            }

            return components
        },
        formatAll() {
            let text = Object.keys(this.getComponents).length > 0 ? this.data : DOMPurify.sanitize(this.data);
            text = this.replaceNewlines(text);
            text = formatImages(text, this.book, this.id, this.mode)
            text = formatLinks(text)
            text = this.videoInject(text)
            text = this.formatMathJax(text)

            return text
        },
        isCoderunner() {
            return /<pre/i.test(this.data)
        },
    },
    data() {
        return {
            localComponents: {}
        }
    },
    methods: {
        formatImages,
        decodeHtmlEntities(htmlString) {
            const textarea = document.createElement('textarea');
            textarea.innerHTML = htmlString;
            return textarea.value;
        },
        replaceNewlines(text) {
            if (!text) return ''

            text = text.toString()

            if (this.replaceN) {
                let result = ''
                let insidePre = false

                let parts = text.split(/(<\/?pre[^>]*>|<\/?table[^>]*>)/)

                parts.forEach(part => {
                    if (part.startsWith('<pre') || part.startsWith('<table')) {
                        insidePre = true
                        result += part
                    } else if (part.startsWith('</pre') || part.startsWith('</table')) {
                        insidePre = false
                        result += part.replaceAll('\n', '<br>')
                    } else if (insidePre) {
                        result += part
                    } else {
                        result += part.replaceAll('\n', '<br>')
                    }
                });

                return result
            } else {
                return text
            }
        },
        videoInject(html) {
            if (!html) return ''

            html = html.toString()

            const regex = /<a\s[^>]*href="([^"]*(?:\.mp4|\.fmp4|\.webm|\.ogv|\.m4v|\.mov|\.mkv|\.aac|\.flac|\.mp3|\.m4a|\.oga|\.ogg|\.wav)[^"]*)"[^>]*>([^>]*)<\/a>/gm;
            const videosLinks = [...html.matchAll(regex)]

            videosLinks.forEach((link) => {
                html = html.replace(link[0], `<br /><video controls class="w-full rounded-lg"><source src="${link[1]}" />${link[0]}</video><br/>`)
            })

            return html
        },
        formatMathJax(html) {
            if (!html) return ''

            html = html.toString()

            const latexRegex = /(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|\\begin\{.*?\}[\s\S]*?\\end\{.*?\})/gm;
            const htmlTagRegex = /<[^>]+>/;
            let index = 1
            let localComponents = {}

            html = html.replaceAll(latexRegex, (x) => {
                // Проверяем, есть ли внутри формулы HTML-теги
                if (htmlTagRegex.test(x)) {
                    return x; // Пропускаем замену — возвращаем оригинал
                }
                x = this.decodeHtmlEntities(x)

                if (x.startsWith("\\(")) {
                    x = x.replace(/^\\\(/, '\\begin{equation}\n')
                    x = x.replace(/\\\)$/, '\\end{equation}')
                }
                const componentName = `mathjax${index}`
                // Mark raw for avoiding performance overhead. See https://vuejs.org/api/reactivity-advanced#markraw
                localComponents[componentName] = markRaw({
                    render: () => h(VueMathjax, {
                        formula: x,
                        class: 'inline-block',
                        'data-testid': `mathjax-${x}`
                    })
                })
                index++

                return `<${componentName}/>`
            })

            this.localComponents = Object.assign(this.localComponents, localComponents)

            return html
        },
        filtersInject() {
            replaceRutubePrivateLinks()
            replaceRutubeLinks()
        }
    },
    created() {
        this.filtersInject()
    },
    mounted() {
        this.filtersInject()
    },
    updated() {
        this.filtersInject()
    }
})
</script>
